import React, { useState, useRef } from "react";
import { Modal, Form, ProgressBar } from "react-bootstrap";

const PhotoUploadModal = ({
  show,
  onHide,
  onUpload,
  uploading,
  error,
}) => {
  const [photoFile, setPhotoFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = () => {
    if (photoFile) {
      const formData = new FormData();
      formData.append("photo", photoFile);
      if (caption.trim()) {
        formData.append("caption", caption.trim());
      }
      onUpload(formData);
    }
  };

  const handleClose = () => {
    setPhotoFile(null);
    setCaption("");
    setPreview(null);
    onHide();
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" dialogClassName="modal-logut" animation={false}>
      <Modal.Header closeButton style={{ backgroundColor: "#E8E8E8", borderBottom: "1px solid #dbdee0" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>
          <i className="fa fa-camera me-2"></i>
          Upload Photo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger mb-3">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error.message || "Error uploading photo"}
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Preview or Upload Area */}
        {preview ? (
          <div className="text-center mb-3">
            <div className="position-relative d-inline-block">
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  borderRadius: "4px",
                  border: "1px solid #dbdee0",
                }}
              />
              <button
                className="btn btn-danger btn-sm position-absolute"
                style={{ top: "8px", right: "8px" }}
                onClick={clearPhoto}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>
        ) : (
          <div
            className="text-center p-5 mb-3"
            style={{
              border: "2px dashed #dbdee0",
              borderRadius: "4px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <i className="fa fa-cloud-upload fa-3x mb-3 text-muted"></i>
            <p className="mb-3 text-muted">Select a photo to upload</p>
            <div className="d-flex justify-content-center gap-2">
              <button className="btn btn-primary" onClick={handleCameraCapture}>
                <i className="fa fa-camera me-2"></i>
                Take Photo
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fa fa-folder-open me-2"></i>
                Browse Files
              </button>
            </div>
          </div>
        )}

        {/* Caption */}
        <Form.Group>
          <Form.Label className="small">Caption (optional)</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption for this photo..."
            disabled={uploading}
          />
        </Form.Group>

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-3">
            <ProgressBar animated now={100} label="Uploading..." />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose} disabled={uploading}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSubmit}
          disabled={!photoFile || uploading}
        >
          {uploading ? (
            <>
              <i className="fa fa-spinner fa-spin me-1"></i>
              Uploading...
            </>
          ) : (
            <>
              <i className="fa fa-upload me-1"></i>
              Upload Photo
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhotoUploadModal;
